/* eslint-disable max-lines */
import { useNavigate, useParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useQuery } from "@tanstack/react-query";
import { getDbtModelsList, getDbtJobFilters, getDbtTagFilters } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { DataTable } from "@/components/tables";
import { FilterType, TableHeaderType, parseSortDirection } from "@/components/tables/types";
import { DbtModel } from "@/pages/query/types";
import { FilterTagWrapper } from "@/components/tags";
import { FilterApplicableFor } from "@/components/tags/types";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import dayjs from "dayjs";
import { ExecutionStatusCount } from "@/pages/query/ExecutionStatusCount";
import { GET_DBT_JOB_MODELS } from "@/pages/query/constants";
import { toast, ToastAction, Alert, AlertDescription, AlertTitle } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import usePaginate from "@/components/paginate/usePaginate";
import { Paginate } from "@/components/paginate";
import { useState, useEffect } from "react";
import ColorInsightTags from "@/components/tags/ColorInsightTags";
import { DbtCommandHighlight } from "./DbtCommandHighlight";
import { Tooltip } from "@altimateai/lego";

export const DbtJobModels = () => {
  const { workflowId } = useParams();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { currency, enableWarehouseSizeRecommendation, enableDbtJobsListing } = useAppState();
  const { startDate, endDate } = useDateFromSearchParams();
  const navigate = useNavigate();
  const { page, setPage, pageSize, updatePageSize } = usePaginate({ page: 0, size: 10 });
  const [totalModels, setTotalModels] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const sortAttribute = searchParams.get("sortAttribute") || "start_time";
  const sortOrder = parseSortDirection(searchParams.get("sortOrder"), "desc");

  // Read filters from URL search params
  const taskTypeFilter = searchParams.getAll("task_type") || [];
  const commandFilter = searchParams.getAll("command") || [];
  const tagFilter = searchParams.getAll("tag") || [];
  const encodedWorkflowId = workflowId ? encodeURIComponent(workflowId) : "";
  const {
    data: models,
    isFetching,
    error,
    isError,
  } = useQuery({
    queryKey: [
      GET_DBT_JOB_MODELS,
      workflowId,
      startDate,
      endDate,
      page,
      pageSize,
      sortAttribute,
      sortOrder,
      taskTypeFilter,
      commandFilter,
      tagFilter,
    ],
    queryFn: () => getDbtModelsList(encodedWorkflowId, getQueryParams()),
    enabled: Boolean(workflowId),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (models) {
      setTotalModels(models?.total ?? 0);
      setTotalPages(models?.pages ?? 0);
    }
  }, [models]);

  const getQueryParams = () => {
    const params: Record<string, string | number | string[]> = {};
    params["page"] = page + 1;
    params["size"] = pageSize;
    if (startDate) {
      params.start_date = getDateByStartOfDayWithoutTimeoffset(startDate).toISOString();
    }
    if (endDate) {
      params.end_date = getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() ?? "";
    }
    const runId = searchParams.get("runId");
    if (runId) {
      params.run_id = runId;
    }
    if (sortAttribute) {
      params.sortAttribute = sortAttribute;
      params.sortOrder = sortOrder;
    }
    if (taskTypeFilter.length > 0) {
      params.task_type = taskTypeFilter;
    }
    params.command = commandFilter;
    if (tagFilter.length > 0) {
      params.tag = tagFilter;
    }
    return params;
  };

  const handleNavigate = (item: DbtModel) => {
    if (item.dbt_model_present) {
      navigate(`/dbt_models/${btoa(item.task_id)}?tab=4`);
    } else {
      toast({
        title: `Could not find "${item.task_name}" in the integration`,
        description: (
          <Stack direction={"column"} gap={"md"} className={"al-w-full"}>
            <span>
              This could be due to manifest not uploaded to Altimate or due to outdated manifest.
            </span>
            <ToastAction
              className={"al-text-white hover:al-text-black"}
              altText="Upload Manifest"
              onClick={() =>
                navigate(
                  `/settings/integrations/${item.integration_id}/${item.integration_environment}`
                )
              }
            >
              Upload
            </ToastAction>
          </Stack>
        ),
        variant: "destructive",
      });
    }
  };

  const headers: TableHeaderType<DbtModel>[] = [
    {
      id: 1,
      label: "Model Name",
      uid: "task_name",
      width: 200,
      render: (item: DbtModel) => (
        <span
          role="button"
          tabIndex={0}
          className="al-text-brand al-cursor-pointer"
          onClick={() => handleNavigate(item)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleNavigate(item);
            }
          }}
        >
          {item.task_name.split("/").pop()?.split(".")[2] || item.task_name}
        </span>
      ),
    },
    ...(enableDbtJobsListing
      ? [
          {
            id: 2,
            label: "Job",
            uid: "commands",
            width: 300 as const,
            filter: {
              filterType: FilterType.API_DROPDOWN as FilterType.API_DROPDOWN,
              searchKey: "command",
              label: "Job",
              apiFunction: (params: { prefix?: string; page?: number; size?: number }) =>
                getDbtJobFilters(encodedWorkflowId, {
                  page: params.page ?? 1,
                  size: params.size ?? 50,
                  prefix: params.prefix,
                }),
              extractorFunction: (data: unknown) =>
                ((data as { items: { command_display: string }[] }).items ?? []).map(item => ({
                  label: item.command_display,
                  value: item.command_display,
                  showTooltip: true,
                })),
              seeMore: true,
              pageSize: 50,
            },
            render: (item: DbtModel) => {
              if (!item.commands?.length) return <span>-</span>;
              const display = item.commands[0];
              const tooltipContent = (
                <Stack
                  direction="column"
                  gap="xs"
                  className="al-max-w-lg al-break-words al-whitespace-pre-wrap"
                >
                  {item.commands.map((cmd, i) => (
                    <DbtCommandHighlight key={i} command={cmd} />
                  ))}
                </Stack>
              );
              return (
                <Tooltip title={tooltipContent}>
                  <div className="al-truncate al-overflow-hidden">
                    <DbtCommandHighlight command={display} />
                    {item.commands.length > 1 && (
                      <span className="al-text-xs al-text-gray-500 al-ml-1">
                        +{item.commands.length - 1}
                      </span>
                    )}
                  </div>
                </Tooltip>
              );
            },
          },
        ]
      : []),
    {
      id: 3,
      label: "Type",
      uid: "task_type",
      width: 120,
      render: (item: DbtModel) => <span className="al-capitalize">{item.task_type || "-"}</span>,
      filter: {
        filterType: FilterType.DROPDOWN,
        label: "Type",
        options: [
          { label: "Model", value: "model" },
          { label: "Seed", value: "seed" },
          { label: "Snapshot", value: "snapshot" },
          { label: "Test", value: "test" },
        ],
        value: taskTypeFilter,
        onChange: value => {
          updateSearchParamsByKey({
            task_type: value.length > 0 ? value : undefined,
          });
          setPage(0);
        },
        popoverContentProps: {
          avoidCollisions: true,
        },
      },
    },
    {
      id: 5,
      label: "Integration Name",
      uid: "integration_name",
      width: 180,
      render: (item: DbtModel) => <span>{item.integration_name ?? "-"}</span>,
    },
    ...(enableDbtJobsListing
      ? [
          {
            id: 11,
            label: "Tags",
            uid: "tags",
            width: 200 as const,
            filter: {
              filterType: FilterType.API_DROPDOWN as FilterType.API_DROPDOWN,
              searchKey: "tag",
              label: "Tags",
              apiFunction: (params: { prefix?: string; page?: number; size?: number }) =>
                getDbtTagFilters(encodedWorkflowId, {
                  page: params.page ?? 1,
                  size: params.size ?? 50,
                  prefix: params.prefix,
                }),
              extractorFunction: (data: unknown) =>
                ((data as { items: { tag: string }[] }).items ?? []).map(item => ({
                  label: item.tag,
                  value: item.tag,
                })),
              seeMore: true,
              pageSize: 50,
            },
            render: (item: DbtModel) =>
              item.tags?.length ? <ColorInsightTags tags={item.tags} showMore /> : null,
          },
        ]
      : []),
    {
      id: 4,
      label: "Run Cost",
      uid: "query_cost",
      width: 130,
      render: (item: DbtModel) => <div>{formatCost(item.query_cost, currency)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    {
      id: 6,
      label: "Bytes Scanned",
      uid: "bytes_scanned",
      width: 150,
      render: (item: DbtModel) => <div>{formatBytes(item.bytes_scanned)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    {
      id: 7,
      label: "Start Time",
      uid: "start_time",
      width: 180,
      render: (item: DbtModel) => <div>{dayjs(item.start_time).format("YYYY-MM-DD hh:mm a")}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    {
      id: 8,
      label: "Execution Time",
      uid: "execution_time",
      width: 160,
      render: (item: DbtModel) => <div>{formatMilliseconds(item.execution_time)}</div>,
      sort: {
        sortSearchKey: "sortAttribute",
      },
    },
    ...(enableWarehouseSizeRecommendation
      ? [
          {
            id: 9,
            label: "Opportunities",
            uid: "insights",
            render: (item: DbtModel) => <ColorInsightTags tags={item.insights} showMore />,
          },
        ]
      : []),
    {
      id: 10,
      label: "Run status",
      uid: "task_status",
      width: 150,
      render: (item: DbtModel) => (
        <ExecutionStatusCount run_count={item.total_count} success_count={item.success_count} />
      ),
    },
  ];

  return (
    <Stack direction={"column"} gap={"md"}>
      <FilterTagWrapper
        applicableFor={FilterApplicableFor.DBT_JOB_MODELS}
        filters={{
          taskTypeFilter: {
            filterStr: taskTypeFilter,
            label: "Type",
            searchKey: "task_type",
            onclose: (removedFilter?: string) => {
              updateSearchParamsByKey({
                task_type: taskTypeFilter.filter(t => t !== removedFilter),
              });
            },
          },
        }}
      />
      <Stack justify="end">
        <div id="dbt-tab-filter" />
      </Stack>
      {isError ? (
        <Alert variant="destructive" role="alert">
          <AlertTitle>Error loading dbt models</AlertTitle>
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : "Failed to load dbt models. Please try again."}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <DataTable
            isLoading={isFetching}
            onRowClick={() => {}}
            resetPage={() => {}}
            data={{
              items: models?.items ?? [],
            }}
            header={headers}
            defaultSort={{ sortParamValue: "start_time", order: "desc" }}
            minWidth={1800}
            applicableFor={FilterApplicableFor.DBT_JOB_MODELS}
            {...(enableDbtJobsListing ? { portals: { filter: "dbt-tab-filter" } } : {})}
          />
          <Paginate
            isLoading={isFetching}
            itemCount={totalModels}
            page={page}
            pageSize={pageSize}
            numPages={totalPages}
            onPageClick={setPage}
            onPageSizeChange={updatePageSize}
            showPageSize
          />
        </>
      )}
    </Stack>
  );
};
